import { Box, BoxProps, IconButton, styled } from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import React, { useCallback, useState } from 'react';
import { Header } from './Header';
import TooltipHardened from './TooltipHardened';
import ContentControlProvider from '../Stores/ContentControlProvider';
import useNavigation from '../Providers/NavigationProvider';

const contentPadding = 3;

const ScrollButton = styled(IconButton)(({ theme }) => ({
  zIndex: 1250,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  transition: theme.transitions.create(['bottom'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration['leavingScreen'],
  }),
}));

interface Props extends BoxProps {
  title: string;
}

export const Body = ({ title, children, sx }: Props): React.JSX.Element => {
  const { scrollRef, scrollTop, isOpen } = useNavigation();
  const [isHeaderSticky, setHeaderSticky] = useState<boolean>(false);

  /** on mount / unmount */
  const onScroll = useCallback(
    (e: { currentTarget: { scrollTop: number } }) => {
      const { scrollTop } = e.currentTarget;

      const shouldSticky = scrollTop > 172;

      if (shouldSticky !== isHeaderSticky) {
        setHeaderSticky(shouldSticky);
      }
    },
    [isHeaderSticky, setHeaderSticky],
  );

  return (
    <Box
      component={'div'}
      ref={scrollRef}
      onScroll={onScroll}
      sx={{
        width: '100%',
        height: '100vh',
        overflowY: 'scroll',
        overflowX: 'hidden',
        ...sx,
      }}
    >
      <Header key={`header-${title}`} title={title} sticky={isHeaderSticky && false} />
      <Box sx={{ marginTop: -14, px: contentPadding, pb: contentPadding }}>
        <ContentControlProvider>{children}</ContentControlProvider>
      </Box>
      <TooltipHardened tooltip={'Scroll to Top'} placement={'top'} enterDelay={500} leaveDelay={200}>
        <ScrollButton
          onClick={() => scrollTop()}
          size={'large'}
          sx={{
            position: 'fixed',
            left: isOpen ? 230 : 56,
            bottom: isHeaderSticky ? 5 : -56,
          }}
        >
          <ArrowUpward />
        </ScrollButton>
      </TooltipHardened>
    </Box>
  );
};
